import { useState, useEffect, createContext, useContext } from "react";
import { affiliateAPI } from "../services/api";

/**
 * Referral Context - Manages referral code detection and welcome message
 * Detects ?ref=CODE in URL, validates it, and provides referrer info
 */
const ReferralContext = createContext(null);

export const useReferral = () => useContext(ReferralContext);

export const ReferralProvider = ({ children }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectAndValidateReferral();
  }, []);

  const detectAndValidateReferral = async () => {
    try {
      // Check URL for referral code
      const params = new URLSearchParams(window.location.search);
      let code = params.get("ref");

      // Also check localStorage for previously stored code
      const storedCode = localStorage.getItem("reclaim_referral_code");
      
      if (!code && storedCode) {
        code = storedCode;
      }

      if (code) {
        // Validate the code
        const res = await affiliateAPI.validateCode(code);
        
        if (res.data?.valid) {
          const data = {
            code: code.toUpperCase(),
            referrerName: res.data.referrer_name,
            partnerInvitation: res.data.partner_invitation || null,
            isPartner: !!res.data.is_partner,
            message: res.data.message
          };
          
          setReferralData(data);
          
          // Store in localStorage for registration
          localStorage.setItem("reclaim_referral_code", code.toUpperCase());
          
          // Track the click (only if from URL, not localStorage)
          if (params.get("ref")) {
            try {
              await affiliateAPI.trackClick(code, "landing_page");
            } catch (e) {
              // Silently fail tracking
            }
            
            // Clean up URL (remove ?ref= param)
            const newUrl = window.location.pathname;
            window.history.replaceState({}, "", newUrl);
          }
        } else {
          // Invalid code - clear storage
          localStorage.removeItem("reclaim_referral_code");
        }
      }
    } catch (e) {
      console.error("Referral detection error:", e);
    } finally {
      setLoading(false);
    }
  };

  const clearReferral = () => {
    setReferralData(null);
    localStorage.removeItem("reclaim_referral_code");
  };

  const getReferralCode = () => {
    return referralData?.code || localStorage.getItem("reclaim_referral_code");
  };

  return (
    <ReferralContext.Provider value={{
      referralData,
      loading,
      clearReferral,
      getReferralCode,
      hasReferral: !!referralData
    }}>
      {children}
    </ReferralContext.Provider>
  );
};

export default ReferralProvider;
